import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Tooltip from 'antd/lib/tooltip';
import React, { useState } from 'react';
import { mapCsvImportSiteRapToCreateSiteRapDto } from '../../domain/site-rap/mapCsvImportSiteRapToCreateSiteRapDto';
import { useSiteRapUpsertMutation } from '../../domain/site-rap/queries';
import { Import, useImportContext } from '../import';
import { ParsedRecord } from '../import/ImportParser';
import { SITE_RAPS_CSV_CONFIG, SiteRapsCsvColumns } from './constants';
import { siteRapsParser } from './siteRapsParser';
import { useDownloadSiteRapsTemplate } from './useDownloadSiteRapsTemplate';

interface ISiteRapsImportModalProps {
    companyId: number;
    onClose: (actionTaken: boolean) => void;
}

const SiteRapsImportModalWrapped = ({ companyId, onClose }: ISiteRapsImportModalProps) => {
    const {
        viewState: { view, records },
        setImportProgress,
        importErrors,
        setImportErrors,
        importHasErrors,
    } = useImportContext();

    const { mutateAsync: upsertSiteRap } = useSiteRapUpsertMutation();
    const [loading, setLoading] = useState<boolean>(false);

    const handleImportSubmit = async (records: ParsedRecord<SiteRapsCsvColumns>[]) => {
        if (!records.length) return;

        let imported = 0;
        let failed = 0;
        let total = records.length;
        setImportProgress({ imported, failed, total });

        setLoading(true);
        let row = 0;
        for (let record of records) {
            try {
                const dto = mapCsvImportSiteRapToCreateSiteRapDto(record);

                await upsertSiteRap(dto);

                imported++;
                setImportProgress({ imported, failed, total });
            } catch (error: any) {
                failed++;
                setImportProgress({ imported, failed, total });
                importErrors[row] = error.message || 'Import failed!';
                setImportErrors(importErrors);
            }
            row++;
        }

        setLoading(false);
    };

    const handleImportCancel = () => {
        const actionTaken = !!records.length;
        // if imported some data (action taken) - return true,
        // so that parent component can refresh data
        onClose(actionTaken);
    };

    function onDownloadImportTemplateRender() {
        return (
            <p className="ant-upload-hint" key="download-template-section">
                Download a template file &nbsp;
                <DownloadSiteRapsTemplateButton key="import-download-template-button" companyId={companyId} />
            </p>
        );
    }

    const modalActions = [
        ...(importHasErrors ? [<Import.DownloadErrorsButton key="import-download-errors" />] : []),
        <Button key="import-cancel-button" disabled={loading} onClick={handleImportCancel}>
            Close
        </Button>,
        <Import.SubmitButton<SiteRapsCsvColumns> key="import-submit-button" onClick={handleImportSubmit} />,
    ];

    return (
        <Modal
            title="Import Site RAPs"
            width="80%"
            open
            keyboard={false}
            destroyOnHidden
            closable={!loading}
            maskClosable={false}
            onCancel={handleImportCancel}
            footer={modalActions}
        >
            <div>
                {view === 'initial' && <Import.UploadZone acceptFileType=".csv" />}
                {view === 'initial' && <Import.ImportTemplate onRender={onDownloadImportTemplateRender} />}
                {view === 'initial' && <Import.FileDescription />}
                {['preview', 'importing', 'completed'].includes(view) && <Import.Preview<SiteRapsCsvColumns> />}
                {['importing', 'completed'].includes(view) && <Import.Progress />}
                <Alert
                    showIcon
                    message="Site RAP information that synced from salesforce cannot be overridden by import!"
                    type="warning"
                    style={{ margin: '8px 0' }}
                />
                {view === 'completed' && <Import.CompletedMessage />}
            </div>
        </Modal>
    );
};

export const SiteRapsImportModal = (props: ISiteRapsImportModalProps) => {
    return (
        <Import.Provider<SiteRapsCsvColumns> csvConfig={SITE_RAPS_CSV_CONFIG} parser={siteRapsParser} skipHeaderRows={2}>
            <SiteRapsImportModalWrapped {...props} />
        </Import.Provider>
    );
};

interface IDownloadSiteRapsTemplateButtonProps {
    companyId: number;
}

const DownloadSiteRapsTemplateButton: React.FC<IDownloadSiteRapsTemplateButtonProps> = ({ companyId }) => {
    const { isTemplateLoading, downloadTemplate } = useDownloadSiteRapsTemplate(companyId);

    return (
        <Tooltip title="Download the CSV template and fill it in with your data to import.">
            <Button loading={isTemplateLoading} onClick={() => downloadTemplate()}>
                Download Template
            </Button>
        </Tooltip>
    );
};
